import React, { useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import classnames from 'classnames';

import getCollegeService from '../../../hemServices/get-college-service';
import List from '../../../../../components/List';
import Label from '../../../../../components/Label';
import Icon from '../../../../../components/Icon';
import Input from '../../../../../components/Input';

import messages from '../messages';
import '../../styles.css';

export const SEARCH_RESULTS_ID = 'search-college-results';

const getNodeParents = (elem) => {
  let { target } = elem;
  const parents = [];
  while (target) {
    parents.unshift(target);
    target = target.parentNode;
  }
  return parents;
};

export const debouncedSearchByName = debounce(async ({
  keyword,
  search,
  callback,
  hiidsFilter = [],
}) => {
  const data = await search(keyword);
  /* istanbul ignore else */
  if (Array.isArray(data)) {
    const filtered = data.filter(({ hiid }) => !hiidsFilter.includes(hiid));
    callback(filtered);
  }
}, 500);

export const getSelectionsFromValue = (value) => (value ? JSON.parse(value) : []);

export const getValueFromSelections = (selections) => (Array.isArray(selections) ? JSON.stringify(selections) : '[]');

const SearchColleges = ({
  onChange,
  initialValue,
  formatMessage,
  cyId,
  introMessage,
  placeholderMessage,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [searchName, setSearchName] = useState('');
  const [results, setResults] = useState([]);
  const [selections, setSelections] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const handleContainerRef = useRef(null);

  const collegeService = useMemo(() => getCollegeService(), [getCollegeService]);

  const handleContainerClick = () => {
    const input = handleContainerRef.current.querySelector('input');
    /* istanbul ignore else */
    if (input) {
      input.focus();
    }
  };

  const hideResults = (e) => {
    const nodeParents = getNodeParents(e);
    const isTargetListItem = nodeParents.some((node) => node.className /* istanbul ignore next */&& node.className.includes('search-college-result'));
    /* istanbul ignore else */
    if (!isTargetListItem) {
      setInputValue('');
      setSearchName('');
      setResults([]);
      setSelectedIndex(-1);
    }
  };

  useEffect(() => {
    document.addEventListener('click', hideResults);
    handleContainerRef.current.addEventListener('click', handleContainerClick);

    const initialCollegeSelections = getSelectionsFromValue(initialValue.value);
    setSelections(initialCollegeSelections);

    return () => {
      document.removeEventListener('click', hideResults);
      handleContainerRef.current.removeEventListener('click', handleContainerClick);
    };
  }, []);

  const handleChange = (e) => {
    setInputValue(e.target.value);
    setSearchName(e.target.value);
    const hiidsFilter = selections.map(({ hiid }) => hiid);
    debouncedSearchByName({
      keyword: e.target.value,
      search: collegeService.searchByName,
      callback: setResults,
      hiidsFilter,
    });
  };

  const handleSelect = (e, { hiid }) => {
    /* istanbul ignore next */
    if (selections.find((selection) => selection.hiid === hiid)) return;

    const item = results.find((result) => result.hiid === hiid);

    /* istanbul ignore else */
    if (item) {
      const added = [
        ...selections,
        item,
      ];

      setSelections(added);
      setInputValue('');

      const filtered = results.filter((result) => result.hiid !== hiid);
      setResults(filtered);

      const newValue = getValueFromSelections(added);
      onChange(e, { value: newValue });
    }
  };

  const handleRemove = (e, { hiid }) => {
    const removed = selections.filter((selection) => selection.hiid !== hiid);
    setSelections(removed);

    const newValue = getValueFromSelections(removed);
    onChange(e, { value: newValue });
  };

  const handleInputKeyNav = (e) => {
    /* istanbul ignore else */
    if (!inputValue && selections.length) {
      /* istanbul ignore else */
      if (e.key === 'Backspace') {
        e.preventDefault();
        const last = selections[selections.length - 1];
        handleRemove(e, { hiid: last.hiid });
      }
    }

    /* istanbul ignore next */
    if (!searchName) return;

    const list = document.getElementById(SEARCH_RESULTS_ID);

    /* istanbul ignore next */
    if (!list) return;
    /* istanbul ignore next */
    if (list.childNodes.length === 0) return;

    /* istanbul ignore else */
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      /* istanbul ignore else */
      if (e.key === 'ArrowDown' && selectedIndex < list.childNodes.length) {
        setSelectedIndex(selectedIndex + 1);
      } else if (e.key === 'ArrowUp' && selectedIndex > 0) {
        setSelectedIndex(selectedIndex - 1);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();

      if (selectedIndex === -1) return;

      const selected = list.childNodes[selectedIndex + 1];
      handleSelect(e, { hiid: selected.getAttribute('hiid') });
    }
  };

  const isActive = useMemo(() => searchName.length > 0, [searchName]);

  return (
    <div styleName="search-field-container" ref={handleContainerRef} data-cy={cyId}>
      <p styleName="transfer-info-intro">{formatMessage(messages[introMessage])}</p>
      <div styleName={classnames({ 'select-field': true, 'active': isActive })}>
        <span>
          {
            selections.map((selection) => (
              <Label styleName="selected-pill" key={`pill-${selection.hiid}`}>
                {selection.name} ({selection.state})
                <Icon hiid={selection.hiid} name="delete" onClick={handleRemove} />
              </Label>
            ))
          }
        </span>
        <Input
          aria-label={formatMessage(messages[placeholderMessage])}
          placeholder={formatMessage(messages[placeholderMessage])}
          styleName="art-agr-search-input"
          onChange={handleChange}
          value={inputValue}
          onKeyDown={handleInputKeyNav}
        />
      </div>
      {isActive && (
        <List
          id={SEARCH_RESULTS_ID}
          styleName="search-college-results"
          verticalAlign="middle"
          divided
        >
          <List.Item key="search-college">
            {formatMessage(
              messages.collegeSearchResults,
              { keyword: searchName, results: results.length },
            )}
          </List.Item>
          {results.map(({ hiid, name, state }, index) => (
            <List.Item
              key={`search-result-${hiid}`}
              hiid={hiid}
              tabIndex="0"
              styleName={classnames({ 'search-college-result': true, 'active': index === selectedIndex })}
              onClick={handleSelect}
            >
              <List.Content>
                {name} ({state})
              </List.Content>
            </List.Item>
          ))}
        </List>
      )}
    </div>
  );
};
SearchColleges.propTypes = {
  onChange: PropTypes.func,
  formatMessage: PropTypes.func,
  initialValue: PropTypes.object,
  cyId: PropTypes.string,
  introMessage: PropTypes.string,
  placeholderMessage: PropTypes.string,
};

export default SearchColleges;
